// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.import { Divider, Typography } from '@mui/material';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { format } from 'date-fns';
import { TIMELINE_FILTER_KEYS } from 'features/events/components/TimelineFeed/TimelineFeed.utils';
import { useGetEvents } from 'features/events/hooks/useGetEvents';
import { Timeline } from 'greener-ui-library';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_PREFERENCES } from 'utils/constants';

export interface ChatHistoryProps {
  unitId: string;
  onRender?: () => void;
  onLoadMore?: (fn: () => void) => void;
  onHistoryLoaded?: () => void;
}

export const ChatHistory = memo(({ unitId, onRender, onLoadMore, onHistoryLoaded }: ChatHistoryProps) => {
  const [chatHistoryDays] = useLocalStorage(
    USER_PREFERENCES.CHAT_HISTORY.LOCAL_STORAGE_KEY,
    USER_PREFERENCES.CHAT_HISTORY.DEFAULT_DAYS
  );
  const { t } = useTranslation();
  const historyRangeTimestamp = useMemo(() => Date.now() - chatHistoryDays * 24 * 60 * 60 * 1000, []);
  const eventFilters = useMemo(
    () => [
      {
        filterKey: TIMELINE_FILTER_KEYS.DATE_FROM,
        property: 'timestamp',
        value: format(historyRangeTimestamp, "yyyy-MM-dd'T'HH:mm:ss"),
        compareFnKey: 'ge',
      },
    ],
    [historyRangeTimestamp]
  );

  const {
    historicEvents = [],
    isLoading,
    isFetching,
    hasNextPage,
    currentPage,
    resetPage,
    goToNextPage,
  } = useGetEvents({
    unitId,
    filters: eventFilters,
    reactQueryConfig: { suspense: false },
  });

  useEffect(() => {
    resetPage();
  }, [unitId]);

  useEffect(() => {
    if (onHistoryLoaded && historicEvents.length > 0 && !isLoading) {
      onHistoryLoaded();
    }
  }, [historicEvents.length, onHistoryLoaded, isLoading]);

  useEffect(() => {
    if (!isLoading && historicEvents.length > 0 && currentPage === 1 && onRender) {
      onRender();
    }
  }, [onRender, isLoading, historicEvents, currentPage]);

  useEffect(() => {
    if (onLoadMore) {
      onLoadMore(() => {
        if (hasNextPage && !isLoading) {
          goToNextPage();
        }
      });
    }
  }, [onLoadMore, hasNextPage, isLoading, goToNextPage]);

  return historicEvents.length > 0 ? (
    <Box mb={-1} test-dataid="chat-history">
      <Typography variant="body2" color="text.secondary">
        {t('unitControl:chat.history', { timerange: chatHistoryDays })}
      </Typography>
      {isFetching && (
        <Stack width="100%" direction="row" justifyContent="center">
          <CircularProgress color="secondary" size={20} />
        </Stack>
      )}
      <Timeline
        reversedOrder
        noEventsLabel={t('components:timeline.noEvents')}
        loadMoreLabel={t('components:timeline.loadMore')}
        isLoading={isLoading}
        isLoadingMore={isFetching}
        events={historicEvents as any}
        getDerivedSeverity={(event) => event.severity.toLowerCase() as AlertColor}
        renderEventTitle={(event) => event.severity}
        getTimestamp={(event) => parseInt(event.timestamp)}
        slotProps={{
          timelineItemProps: {
            content: {
              elevation: 2,
            },
          },
        }}
      />
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary" mb={2}>
      {t('unitControl:chat.noHistory', { timerange: chatHistoryDays })}
    </Typography>
  );
});

ChatHistory.displayName = 'ChatHistory';
